import { create } from 'zustand';
import { CENTRAL_REPROCESSAMENTO_STATUS_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoStatus';
import { CENTRAL_REPROCESSAMENTO_TIPOS_ENUM } from '@/constants/central-reprocessamento/ConstantCentralReprocessamentoTipos';
import { ModelCentralReprocessamento } from '@/model/central-reprocessamento/ModelCentralReprocessamento';
import { ModelCentralReprocessamentoFormulario } from '@/model/central-reprocessamento/ModelCentralReprocessamentoFormulario';
import ServiceESocial from '@/services/ServiceESocial';
import { notify } from '@/stores/useToast';
import UtilsMask from '@/utils/UtilsMask';
import UtilsPagination from '@/utils/UtilsPagination';

type Reprocessamento = any;

type Pagination = any;

interface Filtros {
  tipo: string | null;
  status: string | null;
  nrinsc: string[];
  busca: string;
  dataInicio: string | null;
  dataFim: string | null;
}

interface State {
  paginacaoListaReprocessamentos: Pagination;
  listaReprocessamentos: Reprocessamento[];
  totalReprocessamentos: number;
  estaPesquisandoReprocessamentos: boolean;
  estaBuscandoReprocessamento: boolean;
  estaCriandoReprocessamento: boolean;
  estaDuplicandoReprocessamento: boolean;
  estaResolicitandoReprocessamento: boolean;

  // Histórico por empregador (CON-5281) — lista separada da home.
  paginacaoHistorico: Pagination;
  listaHistorico: Reprocessamento[];
  totalHistorico: number;
  estaPesquisandoHistorico: boolean;

  filtros: Filtros;

  setPaginacao: (p: Pagination) => void;
  setPaginacaoHistorico: (p: Pagination) => void;
  setFiltros: (f: Filtros) => void;
  pesquisar: (silenciosamente?: boolean) => Promise<void>;
  pesquisarHistorico: (nrinsc: string, silenciosamente?: boolean) => Promise<void>;
  buscar: (id: number | string, silenciosamente?: boolean) => Promise<Reprocessamento | undefined>;
  criar: (payload: Reprocessamento) => Promise<void>;
  duplicar: (reprocessamento: Reprocessamento) => Promise<void>;
  resolicitar: (id: number, payload: Reprocessamento) => Promise<void>;
  verificarPermissaoSolicitacao: (r: Reprocessamento) => boolean;
  getNomeCompletoReprocessamento: (r: Reprocessamento) => string;
}

// query string dos filtros (Tipo/Status/Empresa/busca livre/período) — concatenada ao
// pageable, mesmo GET. `busca` bate em razão social OU CNPJ raiz (server-side).
function _filtrosQuery(filtros: Filtros): string {
  const params = new URLSearchParams();
  if (filtros.tipo) params.append('tipo', filtros.tipo);
  if (filtros.status) params.append('status', filtros.status);
  filtros.nrinsc.forEach((n) => params.append('nrinsc', n));
  if (filtros.busca) params.append('busca', filtros.busca);
  if (filtros.dataInicio) params.append('data_inicio', filtros.dataInicio);
  if (filtros.dataFim) params.append('data_fim', filtros.dataFim);
  const qs = params.toString();
  return qs ? `&${qs}` : '';
}

export const useCentralReprocessamentoStore = create<State>((set, get) => ({
  paginacaoListaReprocessamentos: UtilsPagination.defaultPagination(),
  listaReprocessamentos: [],
  totalReprocessamentos: 0,
  estaPesquisandoReprocessamentos: false,
  estaBuscandoReprocessamento: false,
  estaCriandoReprocessamento: false,
  estaDuplicandoReprocessamento: false,
  estaResolicitandoReprocessamento: false,

  paginacaoHistorico: UtilsPagination.defaultPagination(),
  listaHistorico: [],
  totalHistorico: 0,
  estaPesquisandoHistorico: false,

  filtros: { tipo: null, status: null, nrinsc: [], busca: '', dataInicio: null, dataFim: null },

  setPaginacao(p) {
    set({ paginacaoListaReprocessamentos: p });
  },

  setPaginacaoHistorico(p) {
    set({ paginacaoHistorico: p });
  },

  setFiltros(f) {
    set({ filtros: f });
  },

  async pesquisar(silenciosamente = false) {
    if (!silenciosamente) set({ estaPesquisandoReprocessamentos: true });
    try {
      const pageable = UtilsPagination.definePaginationQuery(get().paginacaoListaReprocessamentos);
      const { data } = await ServiceESocial.centralReprocessamento.pesquisar(
        `${pageable}${_filtrosQuery(get().filtros)}`,
      );
      set({
        listaReprocessamentos: data.content.map((i: Reprocessamento) =>
          ModelCentralReprocessamento.fromObject(i),
        ),
        totalReprocessamentos: data.totalElements,
      });
    } catch {
      if (!silenciosamente) notify.error('Erro ao pesquisar reprocessamentos.');
    } finally {
      set({ estaPesquisandoReprocessamentos: false });
    }
  },

  async pesquisarHistorico(nrinsc, silenciosamente = false) {
    if (!silenciosamente) set({ estaPesquisandoHistorico: true });
    try {
      const pageable = UtilsPagination.definePaginationQuery(get().paginacaoHistorico);
      const { data } = await ServiceESocial.centralReprocessamento.historico(nrinsc, pageable);
      set({
        listaHistorico: data.content.map((i: Reprocessamento) =>
          ModelCentralReprocessamento.fromObject(i),
        ),
        totalHistorico: data.totalElements,
      });
    } catch {
      if (!silenciosamente) notify.error('Erro ao carregar o histórico de reprocessamentos.');
    } finally {
      set({ estaPesquisandoHistorico: false });
    }
  },

  async buscar(reprocessamentoID, silenciosamente = false) {
    if (!silenciosamente) set({ estaBuscandoReprocessamento: true });
    try {
      const { data } = await ServiceESocial.centralReprocessamento.buscar(reprocessamentoID);
      return data;
    } catch {
      if (!silenciosamente) notify.error('Erro ao buscar reprocessamento.');
      return undefined;
    } finally {
      set({ estaBuscandoReprocessamento: false });
    }
  },

  async criar(payload) {
    set({ estaCriandoReprocessamento: true });
    try {
      await ServiceESocial.centralReprocessamento.criar(payload);
      notify.success('Reprocessamento criado com sucesso!');
    } catch {
      notify.error('Erro ao criar reprocessamento.');
    } finally {
      set({ estaCriandoReprocessamento: false });
    }
  },

  async duplicar(reprocessamento) {
    set({ estaDuplicandoReprocessamento: true });
    const payload = new ModelCentralReprocessamentoFormulario();
    payload.tipo = reprocessamento.tipo;
    payload.nrinsc = [reprocessamento.nrinsc];
    await get().criar(payload);
    set({ estaDuplicandoReprocessamento: false });
  },

  async resolicitar(id, payload) {
    set({ estaResolicitandoReprocessamento: true });
    try {
      await ServiceESocial.centralReprocessamento.resolicitar(id, payload);
      notify.success('Reprocessamento resolicitado com sucesso!');
    } catch {
      notify.error('Erro ao resolicitar reprocessamento.');
    } finally {
      set({ estaResolicitandoReprocessamento: false });
    }
  },

  verificarPermissaoSolicitacao(reprocessamento) {
    if (!reprocessamento) return false;
    return [
      CENTRAL_REPROCESSAMENTO_STATUS_ENUM.CRIADO.getValue(),
      CENTRAL_REPROCESSAMENTO_STATUS_ENUM.ERRO.getValue(),
    ].includes(reprocessamento?.status);
  },

  getNomeCompletoReprocessamento(reprocessamento) {
    return `${reprocessamento.id} - ${CENTRAL_REPROCESSAMENTO_TIPOS_ENUM.get(reprocessamento.tipo).getDescription()} - ${UtilsMask.cnpj(reprocessamento.nrinsc)}`;
  },
}));
